import { FaroImagePreview } from "@components/common/dialog/faro-image-preview";
import { ACTION_BTN_SIZE, ACTION_ICON_SIZE } from "@styles/common-styles";
import { Checkbox, CircularProgress, Skeleton, Text } from "@flat-ui";
import { useDateTime } from "@hooks/use-date-time";
import { Box } from "@mui-wrapper";
import { AttachmentItemActions } from "@pages/project-details/project-markups/sidepanel/attachment/attachment-item-actions";
import { AttachmentThumbnail } from "@pages/project-details/project-markups/sidepanel/attachment/attachment-thumbnail";
import { useRefreshAttachment } from "@pages/project-details/project-markups/sidepanel/use-refresh-attachment";
import { withEllipsis } from "@styles/common-styles";
import { getFileExtension, isImageFileType } from "@utils/file-utils";
import { AnnotationEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useEffect, useState } from "react";
const BYTES = 1024;

/**
 * Component representing an attachment item within the markups side panel.
 *
 * @param {MarkupsSidePanelAttachmentItemProps} props - The properties for the component, including the attachment data.
 * @returns {JSX.Element} The rendered component.
 */
export function MarkupsSidePanelAttachmentItem({
  attachment,
  onCheckboxChange,
  isChecked,
  setSelectedAttachments
}) {
  const {
    refreshAttachmentIfExpired,
    isAttachmentExpired
  } = useRefreshAttachment();
  const [isHovered, setIsHovered] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isPreviewDialogOpen, setIsPreviewDialogOpen] = useState(false);
  const {
    formatDate
  } = useDateTime();
  const {
    trackEvent
  } = useTrackEvent();

  /**
   * It will convert the bytes in MB
   *
   * @param bytes
   * @returns the MB with 2 case decimals
   */
  function bytesToMB(bytes) {
    const megabytes = bytes / (BYTES * BYTES);
    return megabytes.toFixed(2);
  }

  // Get the file extension from the attachment's name and check if it represents an image file type.
  const fileExtension = getFileExtension(attachment.name);
  const isImage = isImageFileType(fileExtension);

  /**
   * Handles the click event on an attachment item by:
   *  - Tracking a `preview attachment` event with file type and extension.
   *  - Opening the image preview dialog if the attachment is an image.
   *  - Opening the attachment URL in a new secure tab if it's not an image.
   */
  function onAttachmentItemClick() {
    if (isImage) {
      trackEvent({
        name: AnnotationEvents.previewAttachment,
        props: {
          fileType: "image",
          fileExtension
        }
      });
      setIsPreviewDialogOpen(true);
    } else {
      trackEvent({
        name: AnnotationEvents.previewAttachment,
        props: {
          fileType: "other",
          fileExtension
        }
      });
      window.open(attachment.signedUri, "_blank", "noopener,noreferrer");
    }
  }
  function handleCheckboxClick(event) {
    event.stopPropagation();
    onCheckboxChange?.(attachment.id, event.target.checked);
  }
  function handleMouseEnter() {
    if (!isPreviewDialogOpen) {
      setIsHovered(true);
    }
  }
  function handleMouseLeave() {
    if (!isPreviewDialogOpen) {
      setIsHovered(false);
    }
  }
  useEffect(() => {
    async function checkAndRefresh() {
      if (isAttachmentExpired(attachment)) {
        try {
          await refreshAttachmentIfExpired([attachment.id]);
        } catch {
          setIsLoaded(true);
        }
      }
      setIsLoaded(true);
    }
    void checkAndRefresh();
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return <Box sx={{
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    borderBottom: 1,
    borderColor: "divider",
    "&:hover": {
      backgroundColor: "surfaces.tertiary",
      cursor: "pointer"
    }
  }} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      {!isLoaded ? <Skeleton variant="rectangular" height="2.75rem" width="100%" sx={{
      marginY: "0.5rem"
    }} /> : <>
          <Checkbox checked={isChecked} onChange={handleCheckboxClick} inputProps={{
        "aria-label": "Select attachment"
      }} controlSx={{
        // Keeps the box off the panel edge and aligned with the select-all checkbox above.
        ml: 1,
        visibility: isHovered || isChecked ? "visible" : "hidden"
      }} />
          <Box data-testid="markup-side-panel-attachment-item-container" sx={{
        display: "flex",
        gap: "8px",
        padding: "8px",
        width: "100%",
        alignItems: "center",
        position: "relative",
        overflow: "hidden"
      }} onClick={() => onAttachmentItemClick()}>
            <AttachmentThumbnail attachment={attachment} />

            <Box sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          flexGrow: 1,
          minWidth: "0",
          maxWidth: "calc(100% - 100px)",
          overflow: "hidden"
        }}>
              <Text variant="bodyS" sx={{
            color: isHovered ? "primary.main" : "text.primary",
            ...withEllipsis
          }}>
                <var>{attachment.name}</var>
              </Text>
              <Box sx={{
            display: "flex",
            gap: "8px"
          }}>
                <Text variant="bodyXS" sx={{
              color: isHovered ? "primary.main" : "text.primary"
            }}>
                  <var>{formatDate(attachment.createdAt)}</var>
                </Text>
                {attachment.fileSize && <>
                    <Text variant="bodyXS" sx={{
                color: isHovered ? "primary.main" : "text.primary"
              }}>
                      .
                    </Text>
                    <Text variant="bodyXS" sx={{
                color: isHovered ? "primary.main" : "text.primary"
              }}>
                      {bytesToMB(attachment.fileSize)} MB
                    </Text>
                  </>}
              </Box>
            </Box>
            {isDownloading && <CircularProgress size={"1rem"} />}

            {isHovered && !isDownloading && <Box onClick={e => e.stopPropagation()} sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: "8px",
          width: "70px",
          flexShrink: 0
        }}>
                <AttachmentItemActions attachment={attachment} setIsDownloading={setIsDownloading} eventSource="side panel" buttonSize={ACTION_BTN_SIZE} iconSize={ACTION_ICON_SIZE} setSelectedAttachments={setSelectedAttachments} />
              </Box>}
          </Box>
          {isPreviewDialogOpen && <FaroImagePreview attachment={attachment} isDownloading={isDownloading} setIsDownloading={setIsDownloading} setIsPreviewDialogOpen={setIsPreviewDialogOpen} setSelectedAttachments={setSelectedAttachments} />}
        </>}
    </Box>;
}